import React from 'react';

const getSelectedSkillLabel = (t, oarsFilter) => {
  if (oarsFilter === 'O' || oarsFilter === 'OQ') return t('chat_oars_filter_open');
  if (oarsFilter === 'A' || oarsFilter === 'AF') return t('chat_oars_filter_affirm');
  if (oarsFilter === 'SR') return t('chat_oars_filter_reflect');
  if (oarsFilter === 'CR') return t('chat_oars_filter_complex_reflect');
  if (oarsFilter === 'S') return t('chat_oars_filter_summarize');
  return t('chat_oars_filter_all');
};

function ChatHeader({
  t,
  isOarsMode,
  isStrategiesMode = false,
  strategyTask = '',
  strategyName = '',
  strategyTurnCount = 0,
  strategyTurnLimit = 10,
  isClientMode,
  llmMode,
  oarsFilter,
  oarsExercise,
  oarsExerciseIndex,
  oarsExerciseTotal,
  counsellorLabel,
  clientHeading,
  profileIdx,
  headingModeLabel,
  useCompactHeader = false,
  dropdownRef,
  handleLlmMode,
  isLlmControlDisabled,
  sessionEnded,
  showCounsellorDropdown,
  setShowCounsellorDropdown,
  counsellorMode,
  sessionCounsellorMode = null,
  counsellorModes,
  setCounsellorMode,
}) {
  const selectedSkillLabel = getSelectedSkillLabel(t, oarsFilter);
  const fullSessionCompactTitle =
    profileIdx === -1
      ? t('chat_client_custom')
      : profileIdx !== null && profileIdx !== undefined
        ? `Client ${profileIdx}`
        : clientHeading;
  const showCompactCounsellorSelector =
    useCompactHeader &&
    !isOarsMode &&
    !isStrategiesMode &&
    !isClientMode;
  const isCompactCounsellorSelectorDisabled =
    Boolean(sessionCounsellorMode) || llmMode || sessionEnded || isLlmControlDisabled;
  const compactSelectedCounsellorMode = sessionCounsellorMode || null;
  const compactSelectedCounsellorCode =
    counsellorModes.find((mode) => mode.value === compactSelectedCounsellorMode)?.shortLabel || '--';

  return (
    <div className="chat-header">
      <div className="chat-header-title-row">
        <h3>
          {isStrategiesMode
            ? useCompactHeader
              ? (strategyName || 'Specific Strategies')
              : `${strategyTask || 'Selected'} Strategy: ${strategyName || '—'}`
            : isOarsMode
            ? useCompactHeader ? selectedSkillLabel : `Selected Skill: ${selectedSkillLabel}`
            : isClientMode
              ? t('chat_header_counsellor_session', { counsellor: counsellorLabel })
              : useCompactHeader
                ? fullSessionCompactTitle
                : t('chat_header_session', { client: clientHeading, mode: headingModeLabel })}
        </h3>
        {isStrategiesMode && !useCompactHeader && (
          <span
            className={`strategy-turn-counter${strategyTurnCount >= strategyTurnLimit - 3 ? ' strategy-turn-counter--warning' : ''}`}
            aria-label={`${strategyTurnCount} of ${strategyTurnLimit} trainee turns used`}
          >
            Turns: {strategyTurnCount} / {strategyTurnLimit}
          </span>
        )}
      </div>
      {showCompactCounsellorSelector && (
        <div className="compact-counsellor-selector" ref={dropdownRef}>
          <button
            type="button"
            className={`compact-counsellor-selector__trigger${showCounsellorDropdown ? ' open' : ''}`}
            onClick={() => setShowCounsellorDropdown((prev) => !prev)}
            title={t('chat_counsellor_select_type')}
            aria-label={t('chat_counsellor_select_type')}
            aria-haspopup="listbox"
            aria-expanded={showCounsellorDropdown}
            disabled={isCompactCounsellorSelectorDisabled}
          >
            <span className="compact-counsellor-selector__label">Counsellor</span>
            <span className="compact-counsellor-selector__code">{compactSelectedCounsellorCode}</span>
            <span className="compact-counsellor-selector__arrow" aria-hidden="true">▾</span>
          </button>
          {showCounsellorDropdown && !isCompactCounsellorSelectorDisabled && (
            <div className="counsellor-dropdown compact-counsellor-dropdown" role="listbox">
              {counsellorModes.map((mode) => (
                <button
                  key={mode.value}
                  type="button"
                  role="option"
                  aria-selected={compactSelectedCounsellorMode === mode.value}
                  className={`counsellor-dropdown-item${compactSelectedCounsellorMode === mode.value ? ' active' : ''}`}
                  onClick={() => {
                    setCounsellorMode(mode.value);
                    setShowCounsellorDropdown(false);
                    handleLlmMode(mode.value);
                  }}
                >
                  <span className="counsellor-dropdown-item-code">{mode.shortLabel}</span>
                  <span className="counsellor-dropdown-item-label">{t(mode.labelKey)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {isOarsMode && oarsExercise && !useCompactHeader && (
        <div className="oars-header-meta">
          {oarsExerciseTotal > 0 && (
            <span className="oars-exercise-counter">
              {t('chat_oars_exercise_counter', { current: oarsExerciseIndex + 1, total: oarsExerciseTotal })}
            </span>
          )}
          {oarsExercise.change_goal && (
            <div className="oars-change-goal-pill">
              <span className="oars-change-goal-pill__label">Change Goal:</span>
              <span className="oars-change-goal-pill__value">{oarsExercise.change_goal}</span>
            </div>
          )}
        </div>
      )}
      {!isOarsMode && !isStrategiesMode && !isClientMode && !useCompactHeader && (
        <div className="counsellor-btn-group" ref={dropdownRef}>
          <button
            className="llm-mode-btn"
            onClick={() => handleLlmMode()}
            disabled={isLlmControlDisabled}
            title={sessionEnded ? t('chat_counsellor_finish_analysis') : t('chat_counsellor_start_auto', { label: counsellorLabel })}
          >
            {llmMode
              ? t('chat_counsellor_running', { label: counsellorLabel })
              : t('chat_counsellor_activate', { label: counsellorLabel })}
          </button>
          <button
            className="llm-mode-dropdown-btn"
            onClick={() => setShowCounsellorDropdown((prev) => !prev)}
            disabled={isLlmControlDisabled}
            title={t('chat_counsellor_select_type')}
            aria-label={t('chat_counsellor_select_type')}
          >
            ▾
          </button>
          {showCounsellorDropdown && (
            <div className="counsellor-dropdown">
              {counsellorModes.map((mode) => (
                <button
                  key={mode.value}
                  className={`counsellor-dropdown-item${counsellorMode === mode.value ? ' active' : ''}`}
                  onClick={() => {
                    setCounsellorMode(mode.value);
                    setShowCounsellorDropdown(false);
                  }}
                >
                  <span className="counsellor-dropdown-item-code">{mode.shortLabel}</span>
                  <span className="counsellor-dropdown-item-label">{t(mode.labelKey)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ChatHeader;
